/**
 * Native session recorder bridge.
 *
 * Capacitor plugin "SessionRecorder":
 *  - Android: `SessionRecorderPlugin` + microphone foreground service
 *    (frontend/native/android/Session*.java)
 *  - iOS: `SessionRecorderPlugin.swift` (AVAudioEngine + background audio mode)
 *
 * Records mono AAC-LC in 60 s chunk files in app-private, backup-excluded
 * storage. Two scopes with separate directories:
 *  - `test` (default): the hidden Phase 0 test screen — 16 kHz / 32 kbps
 *    defaults, auto-resume, deleted 24 h after start, never uploaded.
 *  - `production`: Premium Session Recording — encoding from
 *    `SESSION_RECORDING_AUDIO_FORMAT`, no auto-resume, joined into one `.m4a`
 *    and uploaded part by part by the native plugin (this module only passes
 *    presigned URLs + byte ranges; audio never crosses the JS bridge).
 *    Used through `features/session-recording/data/native-*`.
 * The web build has no implementation — callers must check {@link isSessionRecorderAvailable}.
 */
import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'
import { isNativeApp } from '../utils/platform'
import { SecureStorage } from './secure-storage'

export type RecorderScope = 'test' | 'production'

export type RecorderState =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'paused'
  | 'stopped'
  /** Derived on read: the process died mid-recording; closed chunks are intact. */
  | 'interrupted-by-kill'

export interface RecorderChunk {
  index: number
  file: string
  path: string
  /** Wall-clock (epoch ms) span of the chunk as measured while recording. */
  startMs: number
  endMs: number
  wallMs: number
  /** Audio actually encoded into the file. */
  audioMs: number
  /** max(0, wallMs - audioMs): capture that was dropped. */
  lostMs: number
  bytes: number
}

export interface RecorderInterruption {
  /** call | ascend-call | session-interruption | low-storage | open-failed | write-error |
   *  capture-died | user-pause | route-change | input-silent | input-recovered | config-change … */
  type: string
  detail: string
  atMs: number
  /** True when the entry represents time the recorder was NOT capturing. */
  pausedRecording: boolean
  resumedAtMs?: number
  durationMs?: number
  autoResumed?: boolean
  retries?: number
}

export interface RecorderStatus {
  platform: 'android' | 'ios' | 'unknown'
  scope: RecorderScope
  state: RecorderState
  recordingId: string | null
  startedAtMs: number
  endedAtMs: number
  sampleRate: number
  bitrate: number
  codec: string
  segmentSeconds: number
  maxSeconds: number
  autoResume: boolean
  userPaused: boolean
  /** Reason passed to `pause` for a user pause (e.g. `private_moment`, `notification`). */
  userPauseReason: string | null
  capReached: boolean
  stopReason: string | null
  lastError: string | null
  /** Audio captured so far (excludes pauses). */
  elapsedAudioMs: number
  wallElapsedMs: number
  pausedMs: number
  chunkCount: number
  totalBytes: number
  chunks: RecorderChunk[]
  currentChunk: { index: number; startMs: number; bytes: number } | null
  interruptions: RecorderInterruption[]
  activeInterruption: string | null
  batteryStartPct: number
  batteryEndPct: number
  batteryNowPct: number
  charging: boolean
  input: string
  inputType: string
  availableInputs: string[]
  freeStorageBytes: number | null
  dir: string | null
  /** True when read from the on-disk manifest (no live engine in this process). */
  fromDisk: boolean
}

export interface JoinResult {
  path: string
  bytes: number
  durationMs: number
  chunkCount: number
  chunkDurationSumMs: number
  elapsedMs: number
  /** Unreadable chunks left out (the half-written chunk an app kill leaves). */
  skippedChunks?: number
  /** Production: an existing joined file was returned unchanged (byte-identical for resumed uploads). */
  reused?: boolean
}

/** Content-free summary of a recording directory (`listRecordings`). */
export interface StoredRecording {
  recordingId: string
  /** Manifest state; `interrupted-by-kill` when the process died mid-recording. */
  state: RecorderState | 'unknown'
  /** True for the recording the engine is currently capturing. */
  live: boolean
  startedAtMs: number
  endedAtMs: number
  /** Audio in the closed (playable) chunks. */
  elapsedAudioMs: number
  chunkCount: number
  totalBytes: number
  joinedBytes: number
  stopReason: string | null
}

export interface RecorderNativeReadiness {
  micPermission: 'granted' | 'denied' | 'prompt'
  freeStorageBytes: number | null
  /** The engine is capturing (any scope). */
  active: boolean
  activeRecordingId: string | null
  activeScope: RecorderScope | null
  /** OS view: any call (cellular, VoIP, Ascend) owns the audio (Android mode ≠ NORMAL; iOS CallKit/Chime). */
  callActive: boolean
}

export interface UploadPartResult {
  /** HTTP status of the PUT. */
  status: number
  /** S3 `ETag` response header (quoted, as returned). */
  etag: string | null
}

export interface ProductionStartOptions {
  recordingId: string
  sampleRate: number
  bitrate: number
  segmentSeconds: number
  maxSeconds: number
  /** Stop with `capReached` once the chunks reach this size (keeps the upload under the server cap). */
  maxBytes: number
}

export interface PlayResult {
  path: string
  file: string
  bytes: number
  durationMs: number
}

export interface RecorderLevel {
  rms: number
  peak: number
  db: number
}

export type RecorderEventName =
  | 'level'
  | 'state'
  | 'chunk'
  | 'interruption'
  | 'capReached'
  | 'error'
  | 'playbackEnded'

export interface StartRecordingOptions {
  recordingId?: string
  scope?: RecorderScope
  /** Defaults: 16000 Hz / 32000 bps / 60 s chunks / 100 min hard cap. */
  sampleRate?: number
  bitrate?: number
  segmentSeconds?: number
  maxSeconds?: number
  maxBytes?: number
  /** Resume automatically when an interruption clears (default true for the spike). */
  autoResume?: boolean
}

/** Hard stop for the test recorder: 100 minutes. */
export const RECORDER_MAX_SECONDS = 100 * 60
/** Test recordings are deleted by the native layer this long after they start. */
export const RECORDER_TEST_TTL_MS = 24 * 60 * 60 * 1000

type SessionRecorderPlugin = {
  start(options: StartRecordingOptions): Promise<unknown>
  pause(options?: { reason?: string }): Promise<unknown>
  resume(): Promise<unknown>
  stop(): Promise<unknown>
  status(options?: { scope?: RecorderScope }): Promise<unknown>
  readiness(options?: { requestPermission?: boolean }): Promise<unknown>
  join(options?: { recordingId?: string; scope?: RecorderScope }): Promise<JoinResult>
  uploadPart(options: {
    path: string
    offset: number
    length: number
    url: string
    allowCellular?: boolean
  }): Promise<UploadPartResult>
  listRecordings(options?: { scope?: RecorderScope }): Promise<{ recordings?: unknown }>
  play(options: { which: 'lastChunk' | 'joined'; recordingId?: string }): Promise<PlayResult>
  stopPlayback(): Promise<void>
  deleteRecording(options?: { recordingId?: string; scope?: RecorderScope }): Promise<{ deleted: boolean }>
  deleteAll(options?: { scope?: RecorderScope | 'all' }): Promise<{ removed: number }>
  sweepExpired(options?: { scope?: RecorderScope; maxAgeMs?: number }): Promise<{ removed: number }>
  addListener(
    eventName: RecorderEventName,
    listener: (data: never) => void,
  ): Promise<PluginListenerHandle>
}

const SessionRecorder = registerPlugin<SessionRecorderPlugin>('SessionRecorder')

/** True only in the iOS / Android shells (the web build has no recorder). */
export function isSessionRecorderAvailable(): boolean {
  return isNativeApp()
}

const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)

function normalizeChunk(raw: unknown): RecorderChunk | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const wallMs = num(r.wallMs)
  const audioMs = num(r.audioMs)
  return {
    index: num(r.index),
    file: str(r.file),
    path: str(r.path),
    startMs: num(r.startMs),
    endMs: num(r.endMs),
    wallMs,
    audioMs,
    lostMs: num(r.lostMs, Math.max(0, wallMs - audioMs)),
    bytes: num(r.bytes),
  }
}

function normalizeInterruption(raw: unknown): RecorderInterruption | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const out: RecorderInterruption = {
    type: str(r.type, 'unknown'),
    detail: str(r.detail),
    atMs: num(r.atMs),
    pausedRecording: r.pausedRecording === true,
  }
  if (typeof r.resumedAtMs === 'number') out.resumedAtMs = r.resumedAtMs
  if (typeof r.durationMs === 'number') out.durationMs = r.durationMs
  if (typeof r.autoResumed === 'boolean') out.autoResumed = r.autoResumed
  if (typeof r.retries === 'number') out.retries = r.retries
  return out
}

/** Coerce whatever the native layer returned into a fully-populated status. */
export function normalizeRecorderStatus(raw: unknown): RecorderStatus {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const platform = r.platform === 'ios' || r.platform === 'android' ? r.platform : 'unknown'
  const state = str(r.state, 'idle') as RecorderState
  const chunks = (Array.isArray(r.chunks) ? r.chunks : [])
    .map(normalizeChunk)
    .filter((c): c is RecorderChunk => c !== null)
    .sort((a, b) => a.index - b.index)
  const interruptions = (Array.isArray(r.interruptions) ? r.interruptions : [])
    .map(normalizeInterruption)
    .filter((i): i is RecorderInterruption => i !== null)
  const cur = r.currentChunk as Record<string, unknown> | undefined
  return {
    platform,
    scope: r.scope === 'production' ? 'production' : 'test',
    state,
    recordingId: typeof r.recordingId === 'string' ? r.recordingId : null,
    startedAtMs: num(r.startedAtMs),
    endedAtMs: num(r.endedAtMs),
    sampleRate: num(r.sampleRate, 16000),
    bitrate: num(r.bitrate, 32000),
    codec: str(r.codec),
    segmentSeconds: num(r.segmentSeconds, 60),
    maxSeconds: num(r.maxSeconds, RECORDER_MAX_SECONDS),
    autoResume: r.autoResume !== false,
    userPaused: r.userPaused === true,
    userPauseReason: typeof r.userPauseReason === 'string' ? r.userPauseReason : null,
    capReached: r.capReached === true,
    stopReason: typeof r.stopReason === 'string' ? r.stopReason : null,
    lastError: typeof r.lastError === 'string' ? r.lastError : null,
    elapsedAudioMs: num(r.elapsedAudioMs),
    wallElapsedMs: num(r.wallElapsedMs),
    pausedMs: num(r.pausedMs),
    chunkCount: num(r.chunkCount, chunks.length),
    totalBytes: num(r.totalBytes),
    chunks,
    currentChunk: cur
      ? { index: num(cur.index), startMs: num(cur.startMs), bytes: num(cur.bytes) }
      : null,
    interruptions,
    activeInterruption: typeof r.activeInterruption === 'string' ? r.activeInterruption : null,
    batteryStartPct: num(r.batteryStartPct, -1),
    batteryEndPct: num(r.batteryEndPct, -1),
    batteryNowPct: num(r.batteryNowPct, -1),
    charging: r.charging === true,
    input: str(r.input),
    inputType: str(r.inputType),
    availableInputs: Array.isArray(r.availableInputs)
      ? r.availableInputs.filter((x): x is string => typeof x === 'string')
      : [],
    freeStorageBytes: typeof r.freeStorageBytes === 'number' ? r.freeStorageBytes : null,
    dir: typeof r.dir === 'string' ? r.dir : null,
    fromDisk: r.fromDisk === true,
  }
}

export async function startRecorder(options: StartRecordingOptions = {}): Promise<RecorderStatus> {
  return normalizeRecorderStatus(
    await SessionRecorder.start({ maxSeconds: RECORDER_MAX_SECONDS, ...options }),
  )
}

/**
 * Premium Session Recording capture: server-issued id, caller's encoding, and
 * NO auto-resume (after a call / Siri / mic loss the provider taps Resume).
 */
export async function startProductionRecorder(options: ProductionStartOptions): Promise<RecorderStatus> {
  return normalizeRecorderStatus(
    await SessionRecorder.start({ ...options, scope: 'production', autoResume: false }),
  )
}

/**
 * `ascend-call` / `interruption` pause as an interruption; anything else is a
 * user pause and the reason is kept on the status (`userPauseReason`).
 */
export async function pauseRecorder(reason?: string): Promise<RecorderStatus> {
  return normalizeRecorderStatus(await SessionRecorder.pause({ reason: reason ?? 'user' }))
}

export async function resumeRecorder(): Promise<RecorderStatus> {
  return normalizeRecorderStatus(await SessionRecorder.resume())
}

export async function stopRecorder(): Promise<RecorderStatus> {
  return normalizeRecorderStatus(await SessionRecorder.stop())
}

export async function getRecorderStatus(scope?: RecorderScope): Promise<RecorderStatus> {
  return normalizeRecorderStatus(await SessionRecorder.status(scope ? { scope } : undefined))
}

/** Mic permission (optionally prompting now), free storage, engine busy, call live. */
export async function getRecorderReadiness(
  options: { requestPermission?: boolean } = {},
): Promise<RecorderNativeReadiness> {
  const r = ((await SessionRecorder.readiness(options)) ?? {}) as Record<string, unknown>
  const mic = r.micPermission
  return {
    micPermission: mic === 'granted' || mic === 'denied' ? mic : 'prompt',
    freeStorageBytes: typeof r.freeStorageBytes === 'number' ? r.freeStorageBytes : null,
    active: r.active === true,
    activeRecordingId: typeof r.activeRecordingId === 'string' ? r.activeRecordingId : null,
    activeScope: r.activeScope === 'production' || r.activeScope === 'test' ? r.activeScope : null,
    callActive: r.callActive === true,
  }
}

/**
 * Production: join a recording's chunks into `recording.m4a` without
 * re-encoding. Idempotent — an existing joined file is returned unchanged.
 */
export function joinRecording(recordingId: string): Promise<JoinResult> {
  return SessionRecorder.join({ recordingId, scope: 'production' })
}

/**
 * PUT one byte range of a joined production recording to a presigned S3
 * UploadPart URL (native HTTP; the audio never enters JS). Resolves for any
 * HTTP response; rejects with code `NETWORK` / `FILE` / `NOT_FOUND` otherwise.
 */
export function uploadRecordingPart(options: {
  path: string
  offset: number
  length: number
  url: string
  /** false refuses mobile data for this request (iOS: allowsCellularAccess; Android: pinned to Wi-Fi). */
  allowCellular?: boolean
}): Promise<UploadPartResult> {
  return SessionRecorder.uploadPart(options)
}

function normalizeStoredRecording(raw: unknown): StoredRecording | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.recordingId !== 'string' || !r.recordingId) return null
  return {
    recordingId: r.recordingId,
    state: str(r.state, 'unknown') as StoredRecording['state'],
    live: r.live === true,
    startedAtMs: num(r.startedAtMs),
    endedAtMs: num(r.endedAtMs),
    elapsedAudioMs: num(r.elapsedAudioMs),
    chunkCount: num(r.chunkCount),
    totalBytes: num(r.totalBytes),
    joinedBytes: num(r.joinedBytes),
    stopReason: typeof r.stopReason === 'string' ? r.stopReason : null,
  }
}

/** Recordings on disk for `scope` (content-free; orphan recovery). */
export async function listStoredRecordings(scope: RecorderScope): Promise<StoredRecording[]> {
  const res = await SessionRecorder.listRecordings({ scope })
  const list = Array.isArray(res?.recordings) ? res.recordings : []
  return list.map(normalizeStoredRecording).filter((r): r is StoredRecording => r !== null)
}

/** Delete one production recording (stops it first when it is the live one). */
export async function deleteProductionRecording(recordingId: string): Promise<boolean> {
  const res = await SessionRecorder.deleteRecording({ recordingId, scope: 'production' })
  return res.deleted
}

/** Delete production recordings that started more than `maxAgeMs` ago (never the live one). */
export async function sweepProductionRecordings(maxAgeMs: number): Promise<number> {
  if (!isSessionRecorderAvailable()) return 0
  const res = await SessionRecorder.sweepExpired({ scope: 'production', maxAgeMs })
  return res.removed
}

/** Concatenate the chunks into one file WITHOUT re-encoding. */
export function joinRecorderChunks(): Promise<JoinResult> {
  return SessionRecorder.join()
}

export function playRecorderAudio(which: 'lastChunk' | 'joined'): Promise<PlayResult> {
  return SessionRecorder.play({ which })
}

export function stopRecorderPlayback(): Promise<void> {
  return SessionRecorder.stopPlayback()
}

export async function deleteTestRecording(): Promise<boolean> {
  const res = await SessionRecorder.deleteRecording()
  return res.deleted
}

/** Remove test recordings older than 24 h (native also sweeps at app start). */
export async function sweepExpiredTestRecordings(): Promise<number> {
  if (!isSessionRecorderAvailable()) return 0
  const res = await SessionRecorder.sweepExpired()
  return res.removed
}

/** Per-user upload-queue keys (`<prefix><uid>`) — content-free, wiped with the audio. */
export const SESSION_RECORDING_UPLOAD_QUEUE_KEY_PREFIX = 'ascend-session-recording-uploads:'

const deviceWipeListeners = new Set<() => void>()

/** Notified (synchronously, first) by {@link wipeSessionRecorderDeviceData} — e.g. to drop in-memory queues. */
export function onSessionRecorderDeviceWipe(listener: () => void): () => void {
  deviceWipeListeners.add(listener)
  return () => deviceWipeListeners.delete(listener)
}

/** Device key: the account whose Premium Session Recording audio is on this device. */
export const SESSION_RECORDING_AUDIO_OWNER_KEY = 'ascend-session-recording-audio-owner'

async function removeUploadQueueKeys(keepUserId?: string): Promise<void> {
  const keep = keepUserId ? `${SESSION_RECORDING_UPLOAD_QUEUE_KEY_PREFIX}${encodeURIComponent(keepUserId)}` : null
  const { keys } = await SecureStorage.keys()
  await Promise.all(
    keys
      .filter((k) => k.startsWith(SESSION_RECORDING_UPLOAD_QUEUE_KEY_PREFIX) && k !== keep)
      .map((key) => SecureStorage.remove({ key })),
  )
}

/**
 * Called whenever the signed-in user is resolved. Audio and queues left by a
 * different account (its session expired or was cleared without a sign-out on
 * a shared device) are deleted; the same account signing back in keeps its
 * un-uploaded recordings.
 */
export async function claimSessionRecordingAudio(userId: string): Promise<void> {
  if (!isSessionRecorderAvailable()) return
  const { value } = await SecureStorage.get({ key: SESSION_RECORDING_AUDIO_OWNER_KEY })
  if (value === userId) return
  if (value) {
    await removeUploadQueueKeys(userId)
    await SessionRecorder.deleteAll({ scope: 'production' })
  }
  await SecureStorage.set({ key: SESSION_RECORDING_AUDIO_OWNER_KEY, value: userId })
}

/** Delete only the hidden-test recordings (sign-in page hygiene keeps the account's session audio). */
export async function wipeTestRecordings(): Promise<void> {
  if (!isSessionRecorderAvailable()) return
  try {
    await SessionRecorder.deleteAll({ scope: 'test' })
  } catch (err) {
    const message = (err instanceof Error ? err.message : String(err)).toLowerCase()
    if (!message.includes('not implemented') && !message.includes('not available')) throw err
  }
}

/**
 * Explicit sign-out: stop any running recording and delete every local
 * recording — hidden-test AND Premium Session Recording audio (uploaded or
 * not) — plus the upload queues and the owner marker. Safe on web and on
 * shells that predate the plugin (no-op).
 */
export async function wipeSessionRecorderDeviceData(): Promise<void> {
  if (!isSessionRecorderAvailable()) return
  for (const listener of deviceWipeListeners) {
    try {
      listener()
    } catch {
      /* in-memory state only; the storage + native wipe below still run */
    }
  }
  let queueError: unknown = null
  try {
    await removeUploadQueueKeys()
    await SecureStorage.remove({ key: SESSION_RECORDING_AUDIO_OWNER_KEY })
  } catch (err) {
    queueError = err
  }
  try {
    await SessionRecorder.deleteAll({ scope: 'all' })
  } catch (err) {
    const message = (err instanceof Error ? err.message : String(err)).toLowerCase()
    // An installed shell older than this plugin has nothing to wipe.
    if (!message.includes('not implemented') && !message.includes('not available')) throw err
  }
  if (queueError) throw queueError
}

/** @deprecated Kept for the Phase 0 call sites — wipes test AND production data. */
export const wipeSessionRecorderTestData = wipeSessionRecorderDeviceData

export function onRecorderEvent<T = unknown>(
  name: RecorderEventName,
  handler: (data: T) => void,
): () => void {
  if (!isSessionRecorderAvailable()) return () => undefined
  let handle: PluginListenerHandle | null = null
  let cancelled = false
  void SessionRecorder.addListener(name, handler as (data: never) => void)
    .then((h) => {
      if (cancelled) void h.remove()
      else handle = h
    })
    .catch(() => undefined)
  return () => {
    cancelled = true
    if (handle) void handle.remove()
  }
}

/** True for Capacitor's error when an installed shell predates the plugin. */
export function isSessionRecorderUnavailableError(err: unknown): boolean {
  const message = (err instanceof Error ? err.message : String(err)).toLowerCase()
  return message.includes('not implemented') || message.includes('not available')
}

/** Platform label for reports. */
export function recorderPlatformLabel(): string {
  return Capacitor.getPlatform()
}
